import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ChangeEvent } from 'react';
import { Modal, Table, Input, InputNumber, Button, Tag, message, Alert, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Select as SelectImpl } from '@components/shared/ui/Select';
import { inventoryService as defaultInventoryService } from '@services/operator/inventoryService';
import { renderCustomAttributeValue } from '@utils/customAttributeValue';
import { visibleCustomAttributeEntries } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';

const { Text } = Typography;

export interface InventoryRecord {
  id: string;
  available_quantity?: number;
  quantity?: number;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  batch?: string | null;
  // API 回傳一定有 id/name；code 為選用文字。
  inventory_type?: { id: string; name: string; code?: string | null; [key: string]: unknown };
  // DB schema: custom_attributes jsonb DEFAULT '{}' — 永遠不是 null（hash 或缺 key）
  custom_attributes?: Record<string, unknown>;
  [key: string]: unknown;
}

interface InventoryListResult {
  data?: InventoryRecord[];
  has_more?: boolean;
  since_id?: string | null;
  [key: string]: unknown;
}

interface InventoryServiceLike {
  list: (params: Record<string, unknown>) => Promise<InventoryListResult>;
  [key: string]: unknown;
}

// Mirrors @components/shared/ui/Select's FetchDataParams / FetchDataResult (those interfaces are not exported).
interface ProductFetchParams {
  search?: string;
  sinceId?: unknown;
  pageSize?: number;
}

interface ProductFetchResultForSelect {
  options?: Array<{ value: string; label: string; [key: string]: unknown }>;
  hasMore?: boolean;
  sinceId?: unknown;
}

interface ProductRecord {
  id: string;
  sku: string;
  name: string;
  [key: string]: unknown;
}

interface ProductFetchResult {
  data?: ProductRecord[];
  has_more?: boolean;
  since_id?: string | null;
  [key: string]: unknown;
}

interface ProductServiceLike {
  fetchList: (params: Record<string, unknown>) => Promise<ProductFetchResult | ProductRecord[]>;
  [key: string]: unknown;
}

interface AttributeDefinition {
  name: string;
  label?: string | null;
  [key: string]: unknown;
}

interface SelectedProduct {
  id: string;
  sku: string;
  name: string;
}

export interface MultiSelectPick {
  inventory: InventoryRecord;
  allocatedQuantity: number;
}

interface InsufficientStock {
  available: number;
  target: number;
}

/**
 * 庫存選擇 Modal
 *
 * 上方：Select 元件選商品（跟其他頁面一致的搜尋體驗）
 * 下方：Table 顯示該商品的庫存批次
 *
 * 有 productId 時跳過商品選擇，直接顯示庫存
 */
interface InventorySelectorModalProps {
  visible: boolean;
  onCancel: () => void;
  onSelect?: (inventory: InventoryRecord) => void;
  title?: string;
  excludeIds?: string[];
  productId?: string;
  productName?: string;
  warehouseId?: string | null;
  // Merchant scope — required when caller is editing a merchant-scoped record (e.g. StockAdjustment,
  // InventoryTransformation, StockTransferOrder) so the inventory list is locked to the record's
  // merchant rather than spanning every merchant the operator has access to. Caller passes the
  // record's merchant_id (post useRecordMerchantSync) so multi-merchant operators can't select an
  // inventory belonging to a different merchant. Omitted → no merchant filter at this layer
  // (productService picks merchant via its own context).
  merchantId?: string | null;
  // 接受 Zod-typed service（custom_attributes 是 unknown）與 .js hook 包裝（推為 Object）兩種，
  // 形狀差異在內部呼叫點已有 cast 處理（見 line ~165）。
  inventoryService?: any;
  productService?: any;
  attributeDefinitions?: AttributeDefinition[];
  // ── multi-select mode ──
  multiSelect?: boolean;
  onConfirmMultiple?: (picks: MultiSelectPick[]) => void;
  /**
   * Multi-select mode: prefill "目標數量" when the modal opens.
   * Used by callers replacing an existing batch selection so the user
   * doesn't have to retype the quantity just to reallocate across batches.
   */
  initialTargetQuantity?: number | null;
}

const InventorySelectorModal: React.FC<InventorySelectorModalProps> = ({
  visible,
  onCancel,
  onSelect,
  title,
  excludeIds = [],
  productId: initialProductId,
  productName: initialProductName,
  warehouseId,
  merchantId,
  inventoryService,
  productService,
  attributeDefinitions = [],
  // ── multi-select mode ──
  multiSelect = false,
  onConfirmMultiple,
  initialTargetQuantity = null,
}) => {
  const { t } = useTranslation();
  // 寬泛 Select wrapper 邊界：Select 的 fetchData/value 等 prop shape 與 caller 自定型別會結構性不符，
  // 沿用本 repo 一貫慣例（見 MerchantSelect.tsx / ProductItemCard.tsx）以 ComponentType<any> 包過。
  // 放在 component body 內避免 Vite HMR 下的 top-level TDZ。
  const Select = SelectImpl as React.ComponentType<any>;
  const activeService = inventoryService || defaultInventoryService;

  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const [inventories, setInventories] = useState<InventoryRecord[]>([]);
  const [loadingInventories, setLoadingInventories] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sinceId, setSinceId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [targetQuantity, setTargetQuantity] = useState<number | null>(null);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [insufficientStock, setInsufficientStock] = useState<InsufficientStock | null>(null);

  const hasInitialProduct = !!initialProductId;

  // ── 載入庫存（server-side，支援 search + cursor 分頁）──
  const fetchInventories = useCallback(async (productId: string, search = '', append = false, cursorId: string | null = null) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoadingInventories(true);
    }
    try {
      const params: Record<string, unknown> = { product_id: productId, available: true, page_size: 50 };
      if (warehouseId) params.warehouse_id = warehouseId;
      if (merchantId) params.merchant_id = merchantId;
      if (search) params.search = search;
      if (cursorId) params.since_id = cursorId;
      // activeService may be the default Zod-narrowed inventoryService or a caller-injected
      // service (admin pages); cast to loose shape so both fit InventoryRecord pass-through.
      const result = (await activeService.list(params)) as InventoryListResult;
      const newData: InventoryRecord[] = (result.data as InventoryRecord[] | undefined) || [];

      if (append) {
        setInventories(prev => [...prev, ...newData]);
      } else {
        setInventories(newData);
      }
      setSinceId(result.since_id || null);
      setHasMore(result.has_more || false);
    } catch (error) {
      console.error('Failed to load inventories:', error);
      message.error(t('common.message.fetch_failed') as string);
      if (!append) setInventories([]);
    } finally {
      setLoadingInventories(false);
      setLoadingMore(false);
    }
  }, [activeService, warehouseId, merchantId, t]);

  // ── Reset on open ──
  useEffect(() => {
    if (visible) {
      setSearchText('');
      setInventories([]);
      setSinceId(null);
      setHasMore(false);
      setSelectedRowKeys([]);
      setTargetQuantity(initialTargetQuantity ?? null);
      setAllocations({});
      setInsufficientStock(null);

      if (hasInitialProduct) {
        setSelectedProduct({ id: initialProductId as string, sku: '', name: initialProductName as string });
        fetchInventories(initialProductId as string, '');
      } else {
        setSelectedProduct(null);
      }
    }
  }, [visible, initialProductId]);

  // ── 商品搜尋（Select 元件的 fetchData）──
  const handleFetchProducts = useCallback(async (params: ProductFetchParams): Promise<ProductFetchResultForSelect> => {
    if (!productService) return { options: [] };
    try {
      const result = await productService.fetchList({
        search: params.search,
        limit: params.pageSize,
        since_id: params.sinceId,
      });
      const products: ProductRecord[] = Array.isArray(result) ? result : (result?.data || []);
      const listResult = Array.isArray(result) ? null : result;
      return {
        options: products.map(p => ({
          value: p.id,
          label: `${p.sku} - ${p.name}`,
          sku: p.sku,
          name: p.name,
        })),
        hasMore: listResult?.has_more || false,
        sinceId: listResult?.since_id || null,
      };
    } catch (error) {
      console.error('InventorySelectorModal product search error:', error);
      return { options: [] };
    }
  }, [productService]);

  // ── 選擇商品 → 載入庫存 ──
  const handleProductChange = useCallback((option: { value?: string; sku?: string; name?: string } | null) => {
    if (!option || !option.value) {
      setSelectedProduct(null);
      setInventories([]);
      setSelectedRowKeys([]);
      setAllocations({});
      setTargetQuantity(null);
      setInsufficientStock(null);
      return;
    }
    const product: SelectedProduct = { id: option.value, sku: option.sku || '', name: option.name || '' };
    setSelectedProduct(product);
    setSearchText('');
    setSelectedRowKeys([]);
    setAllocations({});
    setTargetQuantity(null);
    setInsufficientStock(null);
    fetchInventories(option.value, '');
  }, []);

  // ── Search: server-side reload ──
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (selectedProduct) {
        fetchInventories(selectedProduct.id, value);
      }
    }, 400);
  }, [selectedProduct, fetchInventories]);

  // ── Load more ──
  const handleLoadMore = useCallback(() => {
    if (!selectedProduct || !hasMore || loadingMore) return;
    fetchInventories(selectedProduct.id, searchText, true, sinceId);
  }, [selectedProduct, hasMore, loadingMore, searchText, sinceId, fetchInventories]);

  // ── Table columns ──
  const columns = useMemo(() => {
    // .tsx imports antd Table but column shape is heterogeneous (sorter / render returning ReactNode / Tag),
    // any[] keeps the original runtime semantics without forcing antd column generics.
    const cols: any[] = [];

    cols.push({
      title: t('models.inventory_type._'),
      key: 'inventory_type',
      width: 100,
      render: (_: unknown, record: InventoryRecord) => {
        const it = record.inventory_type;
        return it ? (it.code && it.code !== it.name ? it.code : it.name) : '-';
      }
    });

    cols.push({
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      width: 100,
      render: (text: string | null | undefined) => text || '-'
    });

    cols.push({
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 110,
      sorter: (a: InventoryRecord, b: InventoryRecord) => {
        if (!a.expiration_date) return 1;
        if (!b.expiration_date) return -1;
        return a.expiration_date.localeCompare(b.expiration_date);
      },
      render: (text: string | null | undefined) => text || '-'
    });

    cols.push({
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      width: 110,
      sorter: (a: InventoryRecord, b: InventoryRecord) => {
        if (!a.manufacturing_date) return 1;
        if (!b.manufacturing_date) return -1;
        return a.manufacturing_date.localeCompare(b.manufacturing_date);
      },
      render: (text: string | null | undefined) => text || '-'
    });

    // 動態 custom_attributes columns
    if (attributeDefinitions && attributeDefinitions.length > 0) {
      attributeDefinitions.forEach(def => {
        cols.push({
          title: def.label || def.name,
          key: `ca_${def.name}`,
          width: 100,
          render: (_: unknown, record: InventoryRecord) => renderCustomAttributeValue(record.custom_attributes?.[def.name]) || '-'
        });
      });
    } else {
      // 從資料動態推導
      const allKeys = new Set<string>();
      inventories.forEach(inv => {
        visibleCustomAttributeEntries(inv.custom_attributes).forEach(([k]) => allKeys.add(k));
      });
      allKeys.forEach(key => {
        cols.push({
          title: key,
          key: `ca_${key}`,
          width: 100,
          render: (_: unknown, record: InventoryRecord) => renderCustomAttributeValue(record.custom_attributes?.[key]) || '-'
        });
      });
    }

    cols.push({
      title: t('models.inventory.available_quantity', { defaultValue: '可用數量' }),
      key: 'available_quantity',
      width: 90,
      align: 'right',
      sorter: (a: InventoryRecord, b: InventoryRecord) => (a.available_quantity || 0) - (b.available_quantity || 0),
      render: (_: unknown, record: InventoryRecord) => {
        const qty = record.available_quantity ?? record.quantity;
        return <Tag color={(qty ?? 0) > 0 ? 'green' : 'red'}>{qty}</Tag>;
      }
    });

    if (!multiSelect) {
      cols.push({
        title: '',
        key: 'action',
        width: 70,
        render: (_: unknown, record: InventoryRecord) => (
          <Button
            type="primary"
            size="small"
            onClick={() => onSelect?.(record)}
            disabled={excludeIds.includes(record.id)}
          >
            {t('common.button.select')}
          </Button>
        )
      });
    }

    if (multiSelect) {
      cols.push({
        title: t('shared.inventory_selector.allocate_quantity', { defaultValue: 'Allocated Qty' }),
        key: 'allocated',
        width: 110,
        align: 'right',
        render: (_: unknown, record: InventoryRecord) => {
          const isChecked = selectedRowKeys.includes(record.id);
          const max = record.available_quantity ?? 0;
          return (
            <InputNumber
              size="small"
              value={isChecked ? (allocations[record.id] ?? max) : undefined}
              min={1}
              max={max}
              disabled={!isChecked || max <= 0}
              onChange={(val) => {
                if (val == null) return;
                setAllocations((prev) => ({ ...prev, [record.id]: val as number }));
              }}
              style={{ width: '100%' }}
            />
          );
        },
      });
    }

    return cols;
  }, [attributeDefinitions, inventories, excludeIds, onSelect, t, multiSelect, selectedRowKeys, allocations]);

  const rowSelection = multiSelect
    ? {
        type: 'checkbox' as const,
        selectedRowKeys,
        onChange: (keys: React.Key[]) => {
          const nextKeys = keys as string[];
          const prevKeys = selectedRowKeys;
          const added = nextKeys.filter((k) => !prevKeys.includes(k));
          const removed = prevKeys.filter((k) => !nextKeys.includes(k));

          setAllocations((prev) => {
            const next = { ...prev };
            added.forEach((id) => {
              const inv = inventories.find((i) => i.id === id);
              if (inv && next[id] === undefined) {
                next[id] = inv.available_quantity ?? 1;
              }
            });
            removed.forEach((id) => { delete next[id]; });
            return next;
          });

          setSelectedRowKeys(nextKeys);
        },
        getCheckboxProps: (record: InventoryRecord) => ({
          disabled:
            excludeIds.includes(record.id) ||
            (record.available_quantity ?? record.quantity ?? 0) <= 0,
        }),
      }
    : undefined;

  const handleAutoAllocateFefo = useCallback(() => {
    if (!targetQuantity || targetQuantity <= 0) return;

    const eligible = inventories
      .filter((inv) =>
        !excludeIds.includes(inv.id) &&
        (inv.available_quantity ?? 0) > 0,
      )
      .slice()
      .sort((a, b) => {
        const ea = a.expiration_date;
        const eb = b.expiration_date;
        if (!ea && !eb) return 0;
        if (!ea) return 1;
        if (!eb) return -1;
        return ea.localeCompare(eb);
      });

    let remaining = targetQuantity;
    const newKeys: string[] = [];
    const newAllocations: Record<string, number> = {};

    for (const inv of eligible) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, inv.available_quantity ?? 0);
      if (take > 0) {
        newKeys.push(inv.id);
        newAllocations[inv.id] = take;
        remaining -= take;
      }
    }

    setSelectedRowKeys(newKeys);
    setAllocations(newAllocations);

    if (remaining > 0) {
      setInsufficientStock({
        available: targetQuantity - remaining,
        target: targetQuantity,
      });
    } else {
      setInsufficientStock(null);
    }
  }, [targetQuantity, inventories, excludeIds]);

  const handleConfirmMultiple = useCallback(() => {
    if (!onConfirmMultiple || selectedRowKeys.length === 0) return;
    const picked = selectedRowKeys
      .map((id): MultiSelectPick | null => {
        const inv = inventories.find((i) => i.id === id);
        if (!inv) return null;
        const max = inv.available_quantity ?? 0;
        const allocatedQuantity = allocations[id] ?? max;
        return { inventory: inv, allocatedQuantity };
      })
      .filter((x): x is MultiSelectPick => Boolean(x));
    onConfirmMultiple(picked);
  }, [onConfirmMultiple, selectedRowKeys, inventories, allocations]);

  const allocatedTotal = selectedRowKeys.reduce((sum, id) => {
    const inv = inventories.find((i) => i.id === id);
    const max = inv?.available_quantity ?? 0;
    const qty = allocations[id] ?? max;
    return sum + qty;
  }, 0);

  const statusText = targetQuantity
    ? t('shared.inventory_selector.allocated_status', {
        allocated: allocatedTotal,
        target: targetQuantity,
        defaultValue: 'Allocated {{allocated}} / Target {{target}}',
      })
    : t('shared.inventory_selector.allocated_status_no_target', {
        allocated: allocatedTotal,
        defaultValue: 'Allocated {{allocated}}',
      });

  const confirmDisabled =
    selectedRowKeys.length === 0 ||
    selectedRowKeys.some((id) => {
      const inv = inventories.find((i) => i.id === id);
      const max = inv?.available_quantity ?? 0;
      const qty = allocations[id] ?? max;
      return qty <= 0;
    });

  const multiSelectFooter = multiSelect ? (
    <Space>
      <Text type="secondary" style={{ marginRight: 12 }}>
        {statusText}
      </Text>
      <Button onClick={onCancel}>{t('common.button.cancel')}</Button>
      <Button type="primary" disabled={confirmDisabled} onClick={handleConfirmMultiple}>
        {t('shared.inventory_selector.confirm_n_items', {
          count: selectedRowKeys.length,
          defaultValue: 'Confirm Add ({{count}})',
        })}
      </Button>
    </Space>
  ) : null;

  return (
    <Modal
      title={title || (t('shared.inventory_selector.title', '選擇庫存批次') as string)}
      open={visible}
      onCancel={onCancel}
      footer={multiSelect ? multiSelectFooter : null}
      width={900}
      destroyOnClose
      style={{ top: 20 }}
    >
      {/* 商品選擇 — 用 Select 元件（跟其他頁面一致） */}
      {!hasInitialProduct && (
        <div style={{ marginBottom: 16 }}>
          <Select
            placeholder={t('common.placeholder.search_product') as string}
            fetchData={handleFetchProducts}
            pageSize={20}
            isSearchable
            isClearable
            onChange={handleProductChange}
            value={selectedProduct ? { value: selectedProduct.id, label: `${selectedProduct.sku} - ${selectedProduct.name}` } : null}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {/* 庫存批次 Table — 選了商品後顯示 */}
      {selectedProduct && (
        <>
          {multiSelect && (
            <>
              <Alert
                type="info"
                showIcon
                message={t('shared.inventory_selector.multi_select_hint')}
                style={{ marginBottom: 12 }}
              />
              <Space style={{ marginBottom: 12 }}>
                <span>{t('shared.inventory_selector.target_quantity', { defaultValue: 'Target Quantity' })}</span>
                <InputNumber
                  value={targetQuantity}
                  onChange={(v) => setTargetQuantity(v as number | null)}
                  min={1}
                  placeholder=""
                />
                <Button
                  onClick={handleAutoAllocateFefo}
                  disabled={!targetQuantity || targetQuantity <= 0}
                >
                  {t('shared.inventory_selector.auto_allocate_fefo', { defaultValue: 'Auto Allocate (FEFO)' })}
                </Button>
              </Space>
              {insufficientStock && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('shared.inventory_selector.insufficient_stock_warning', {
                    available: insufficientStock.available,
                    target: insufficientStock.target,
                    defaultValue: 'Insufficient stock — can allocate {{available}} / target {{target}}',
                  })}
                  style={{ marginBottom: 12 }}
                  closable
                  onClose={() => setInsufficientStock(null)}
                />
              )}
            </>
          )}

          <div style={{ marginBottom: 12 }}>
            <Input
              placeholder={t('shared.inventory_selector.filter_placeholder', '搜尋批號、效期、屬性...') as string}
              value={searchText}
              onChange={handleSearchChange}
              allowClear
              size="small"
            />
          </div>

          <Table
            columns={columns}
            dataSource={inventories}
            rowKey="id"
            loading={loadingInventories}
            pagination={false}
            size="small"
            scroll={{ y: 'calc(100vh - 360px)' }}
            rowSelection={rowSelection}
            locale={{ emptyText: t('shared.inventory_selector.no_inventory', '此商品沒有可用庫存') }}
          />

          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Button size="small" loading={loadingMore} onClick={handleLoadMore}>
                {t('common.button.load_more', { defaultValue: '載入更多' })}
              </Button>
            </div>
          )}
        </>
      )}

      {/* 未選商品時的提示 */}
      {!selectedProduct && !hasInitialProduct && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          {t('shared.inventory_selector.select_product_first', '請先搜尋並選擇商品')}
        </div>
      )}
    </Modal>
  );
};

export default InventorySelectorModal;
